/* ============================================================
   PeanutPup Landing — interactions
   - FAQ accordion (button + aria-expanded/aria-controls)
   - Sticky mobile add-to-cart bar
   - Pause offscreen autoplay media
   No libraries. Defer-loaded. Safe to include once per page.
   ============================================================ */
(function () {
  'use strict';
  if (window.__pplLandingInit) return;
  window.__pplLandingInit = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------
     FAQ accordions — supports multiple section instances.
     Markup contract:
       .ppl-faq__item > button[data-pp-faq-toggle][aria-controls] + .ppl-faq__a[id]
     ---------------------------------------------------------- */
  function initFaq() {
    document.querySelectorAll('[data-pp-faq-toggle]').forEach(function (btn) {
      if (btn.__pplBound) return;
      btn.__pplBound = true;
      btn.addEventListener('click', function () {
        var item = btn.closest('.ppl-faq__item');
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!item || !panel) return;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        item.classList.toggle('is-open', !open);
        panel.hidden = false; // visibility handled by CSS grid rows; keep in a11y tree while open
        if (open) {
          var hide = function () { if (btn.getAttribute('aria-expanded') === 'false') panel.hidden = true; };
          if (reducedMotion) hide();
          else setTimeout(hide, 260);
        }
      });
    });
  }

  /* ----------------------------------------------------------
     Sticky mobile cart bar.
     - Shows after the hero primary CTA scrolls out of view.
     - Hides again near the footer.
     - Proxies clicks to the hero's native submit button so all
       Dawn product-form behavior (loading state, errors, cart
       notification) is reused. No duplicate cart logic.
     - Mirrors the hero price + submit button state via
       MutationObserver so variant changes stay in sync.
     ---------------------------------------------------------- */
  function initStickyCart() {
    var bar = document.querySelector('[data-pp-sticky-cart]');
    if (!bar) return;

    var heroCta = document.querySelector('[data-pp-primary-cta]');
    var heroSubmit = document.querySelector('[data-pp-product-form] .product-form__submit');
    var heroPrice = document.querySelector('[data-pp-price]');
    var barBtn = bar.querySelector('[data-pp-sticky-submit]');
    var barPrice = bar.querySelector('[data-pp-sticky-price]');
    var footer = document.querySelector('.footer, footer');
    if (!heroCta || !heroSubmit || !barBtn) return;

    var ctaVisible = true;
    var footerVisible = false;

    function render() {
      var show = !ctaVisible && !footerVisible;
      bar.classList.toggle('is-visible', show);
      document.body.classList.toggle('ppl-sticky-active', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      barBtn.tabIndex = show ? 0 : -1;
    }

    var barLabel = bar.querySelector('[data-pp-sticky-label]');

    function syncState() {
      var soldOut = heroSubmit.hasAttribute('disabled');
      barBtn.disabled = soldOut;
      if (barLabel) {
        var label = heroSubmit.querySelector('span');
        if (soldOut) barLabel.textContent = 'Sold out';
        else if (label && label.textContent.trim()) barLabel.textContent = label.textContent.trim();
      }
      if (heroPrice && barPrice) {
        var current = heroPrice.querySelector('.price-item--sale, .price-item--regular');
        if (current) barPrice.textContent = '| ' + current.textContent.trim();
        barPrice.hidden = soldOut;
      }
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { ctaVisible = e.isIntersecting; });
        render();
      }, { rootMargin: '0px 0px -10% 0px' }).observe(heroCta);

      if (footer) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { footerVisible = e.isIntersecting; });
          render();
        }).observe(footer);
      }
    }

    new MutationObserver(syncState).observe(heroSubmit, {
      attributes: true, childList: true, subtree: true, characterData: true
    });
    if (heroPrice) {
      new MutationObserver(syncState).observe(heroPrice, { childList: true, subtree: true, characterData: true });
    }
    syncState();
    render();

    barBtn.addEventListener('click', function () {
      // Delegate to the native Dawn product-form submit.
      heroSubmit.click();
    });
  }

  /* ----------------------------------------------------------
     Autoplay media: only play while on screen; never autoplay
     when the user prefers reduced motion.
     ---------------------------------------------------------- */
  function initMedia() {
    var vids = document.querySelectorAll('video[data-pp-media], .ppl video');
    if (!vids.length) return;
    vids.forEach(function (v) {
      v.muted = true;
      if (reducedMotion) {
        v.removeAttribute('autoplay');
        v.pause();
      }
    });
    if (reducedMotion || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.25 });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* ----------------------------------------------------------
     Bundle cards: swap the hidden variant id and displayed price
     when the card's variant <select> changes. The form itself is a
     native Dawn <product-form>, so add-to-cart is untouched.
     ---------------------------------------------------------- */
  function initBundles() {
    document.querySelectorAll('[data-pp-bundle-card]').forEach(function (card) {
      var select = card.querySelector('[data-pp-bundle-variant-select]');
      var input = card.querySelector('[data-pp-bundle-variant-input]');
      var priceEl = card.querySelector('[data-pp-bundle-price]');
      var btn = card.querySelector('.ppl-bundle__btn');
      if (!select || !input || select.__pplBound) return;
      select.__pplBound = true;

      select.addEventListener('change', function () {
        var opt = select.options[select.selectedIndex];
        input.value = opt.value;
        var available = opt.dataset.available === 'true';
        input.disabled = !available;

        if (priceEl) {
          var html = '';
          if (opt.dataset.compare) html += '<span class="ppl-bundle__was">' + opt.dataset.compare + '</span>';
          html += '<span class="ppl-bundle__now">' + opt.dataset.price + '</span>';
          if (opt.dataset.saved) html += '<span class="ppl-bundle__save">Save ' + opt.dataset.saved + '</span>';
          priceEl.innerHTML = html;
        }
        if (btn) {
          btn.disabled = !available;
          var span = btn.querySelector('span');
          if (span && !available) span.textContent = 'Sold out';
        }
      });
    });
  }

  /* ----------------------------------------------------------
     Offer stack (single SKU): one shared variant <select> drives
     every quantity tier. Prices are pre-formatted server-side per
     variant, so no currency formatting happens in JS.
     ---------------------------------------------------------- */
  function initOfferStack() {
    document.querySelectorAll('[data-pp-offer-stack]').forEach(function (stack) {
      var group = stack.querySelector('[data-pp-offer-variant-select]');
      if (!group || group.__pplBound) return;
      group.__pplBound = true;

      var swatches = group.querySelectorAll('[data-pp-offer-variant-option]');

      swatches.forEach(function (btn) {
        btn.addEventListener('click', function () {
        var id = btn.dataset.variantId;
        var available = btn.dataset.available === 'true';

        swatches.forEach(function (s) {
          var isThis = s === btn;
          s.classList.toggle('is-selected', isThis);
          s.setAttribute('aria-checked', isThis ? 'true' : 'false');
        });

        stack.querySelectorAll('[data-pp-offer-card]').forEach(function (card) {
          var input = card.querySelector('[data-pp-offer-variant-input]');
          if (input) {
            input.value = id;
            input.disabled = !available;
          }

          var blob = card.querySelector('[data-pp-offer-prices]');
          var data = null;
          if (blob) {
            try { data = JSON.parse(blob.textContent)[id]; } catch (e) { data = null; }
          }
          if (data) {
            var set = function (sel, val) {
              var el = card.querySelector(sel);
              if (el && val) el.textContent = val;
            };
            set('[data-pp-offer-net]', data.net);
            set('[data-pp-offer-gross]', data.gross);
            set('[data-pp-offer-unit]', data.unit);
            var cutEl = card.querySelector('[data-pp-offer-cut]');
            if (cutEl && data.cut) cutEl.textContent = 'Save ' + data.cut;
          }

          var tierBtn = card.querySelector('.ppl-offer__btn');
          if (tierBtn) {
            tierBtn.disabled = !available;
            var span = tierBtn.querySelector('span');
            if (span && !available) span.textContent = 'Sold out';
          }
        });
        });
      });
    });
  }

  /* ----------------------------------------------------------
     Direct checkout: inside any [data-pp-direct-checkout] wrapper
     (the homepage featured product), the submit button skips
     add-to-cart entirely and sends the buyer straight to Shopify
     checkout with the selected variant + quantity, via a cart
     permalink (/cart/VARIANT:QTY).
     Registered on click in the CAPTURE phase so it runs before
     product-form.js's submit handler ever fires.
     NOTE: cart permalinks REPLACE the existing cart contents.
     ---------------------------------------------------------- */
  function initDirectCheckout() {
    document.querySelectorAll('[data-pp-direct-checkout]').forEach(function (root) {
      if (root.__pplDC) return;
      root.__pplDC = true;
      root.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('.product-form__submit');
        if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
        var form = btn.closest('form');
        if (!form) return;
        var idInput = form.querySelector('input[name="id"]');
        if (!idInput || !idInput.value) return;

        e.preventDefault();
        e.stopPropagation();

        var qtyInput = form.querySelector('input[name="quantity"], .quantity__input');
        var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

        btn.classList.add('loading');
        var spinner = btn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        window.location.href = cartUrl + '/' + idInput.value + ':' + qty;
      }, true);
    });
  }

  function initAll() {
    initFaq();
    initStickyCart();
    initMedia();
    initBundles();
    initOfferStack();
    initDirectCheckout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-bind after Theme Editor section reloads.
  document.addEventListener('shopify:section:load', initAll);
})();
